import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import BundleTasks from './views/BundleTasks';
import BundleVouchers from './views/BundleVouchers';
import BundleThankyou from './views/BundleThankyou';
import { authenticateUser, isAuthenticated } from './api/auth';
import { fetchBundleTasks } from './api/bundle';
import { resolveBundleCode, storeBundleResult } from './utils/bundles';
import { trackBParamPixel, trackCParamPixel, trackGnParamPixel } from './utils/analytics';
import { ACCESS_CODE_KEY } from './utils/msisdn';

const MAIN_APP_URL = process.env.REACT_APP_MAIN_APP_URL;
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL;
const ROX_BASE_URL = process.env.REACT_APP_ROX_BASE_URL;

const B_PARAM_ENDPOINTS = ['ffw7', 'ffm1', 'pubw7', 'pubw7e', 'pubm1'];
const C_PARAM_ENDPOINTS = ['ffw7', 'ffm1', 'pubw7', 'pubw7e', 'pubm1'];
const URL_PARAMS_TO_STRIP = ['a', 'refferer', 'referer', 'b', 'c', 'gn'];

// Fire and forget - never awaited, errors ignored, never blocks the flow.
// keepalive lets the request finish even if we redirect to the main app right after.
const firePixels = ({ b, c, gn }) => {
  if (B_PARAM_ENDPOINTS.includes(b)) {
    fetch(`${ROX_BASE_URL}/e/${b}`, { method: 'GET', mode: 'no-cors', keepalive: true }).catch(() => {});
    trackBParamPixel(b);
  }
  if (C_PARAM_ENDPOINTS.includes(c)) {
    fetch(`${ROX_BASE_URL}/c/${c}`, { method: 'GET', mode: 'no-cors', keepalive: true }).catch(() => {});
    trackCParamPixel(c);
  }
  if (gn) {
    fetch(`${ROX_BASE_URL}/gn/${gn}`, { method: 'GET', mode: 'no-cors', keepalive: true }).catch(() => {});
    trackGnParamPixel(gn);
  }
};

// Remove a / b / c / gn from the address bar so a refresh or share doesn't re-fire them
const stripUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  if (!URL_PARAMS_TO_STRIP.some((key) => params.has(key))) return;

  URL_PARAMS_TO_STRIP.forEach((key) => params.delete(key));
  const search = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
  );
};

// Back to the main app with the encrypted msisdn as `a`,
// so the main app logs the user in with its normal jt/gen flow
const goToMainApp = () => {
  const accessCode = sessionStorage.getItem(ACCESS_CODE_KEY);
  window.location.replace(
    accessCode ? `${MAIN_APP_URL}/?a=${encodeURIComponent(accessCode)}` : MAIN_APP_URL
  );
};
const goToLogin = () => window.location.replace(LOGIN_URL);

// "/" and any unknown path go back to the main app
// (this also covers navigate('/') inside BundleTasks and BundleThankyou)
const GoToMainApp = () => {
  useEffect(() => {
    goToMainApp();
  }, []);
  return null;
};

const BundleFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const startedRef = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (startedRef.current) return; // StrictMode runs effects twice in dev
    startedRef.current = true;

    const start = async () => {
      const params = new URLSearchParams(location.search);
      const incoming = {
        a: params.get('a'),
        b: params.get('b'),
        c: params.get('c'),
        gn: params.get('gn'),
        refferer: params.get('refferer') || params.get('referer'),
      };

      stripUrlParams();
      firePixels(incoming);

      // 1. Session: swap the encrypted MSISDN for a JWT
      if (incoming.a) {
        const auth = await authenticateUser(incoming.a, incoming.refferer === 'gamenow' ? incoming.refferer : null);
        if (!auth.success) {
          goToLogin();
          return;
        }
      } else if (!isAuthenticated()) {
        goToLogin();
        return;
      }

      // 2. Bundle: a fresh link fetches the bundle and routes by mode.
      //    A refresh on /bundle-tasks etc. has no params - the page reads what is already stored.
      const bundleCode = resolveBundleCode(incoming.b, incoming.c);
      if (bundleCode) {
        const result = await fetchBundleTasks(bundleCode);

        // No bundle / no tasks for this user -> back to the main app, already logged in
        const noTasks = !result?.success || (result.mode === 'task' && !result.tasks?.length);
        if (noTasks) {
          goToMainApp();
          return;
        }

        storeBundleResult(bundleCode, result);
        navigate(result.mode === 'task' ? '/bundle-tasks' : '/bundle-vouchers', {
          replace: true,
          state: { bundleCode, bundle: result },
        });
      } else if (location.pathname === '/') {
        goToMainApp();
        return;
      }

      setReady(true);
    };

    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) {
    return (
      <div className="boot">
        <span className="boot-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/bundle-tasks" element={<BundleTasks />} />
      <Route path="/bundle-vouchers" element={<BundleVouchers />} />
      <Route path="/bundle-thankyou" element={<BundleThankyou />} />
      <Route path="*" element={<GoToMainApp />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <BundleFlow />
    </BrowserRouter>
  );
}

export default App;